import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { authLogout } from '../redux/userRelated/userSlice';
import styled, { keyframes } from 'styled-components';
import { Box, Typography, Button, Paper, Avatar, Fade } from '@mui/material';
import { 
    Logout as LogoutIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); }`;

const PageWrapper = styled(Box)`
    min-height: 80vh;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: transparent;
`;

const PremiumCard = styled(Paper)`
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    padding: 48px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1) !important;
    width: 100%;
    max-width: 480px;
    text-align: center;
    animation: ${fadeIn} 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
`;

const AvatarWrapper = styled(Box)`
    display: flex;
    justify-content: center;
    margin-bottom: 24px;
    position: relative;
    
    &::after {
        content: '';
        position: absolute;
        width: 100px;
        height: 100px;
        background: radial-gradient(circle, rgba(239, 68, 68, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        z-index: 0;
    }
`;

const ActionBtn = styled(Button)`
    border-radius: 14px !important;
    padding: 12px 24px !important;
    font-weight: 800 !important;
    font-size: 1.05rem !important;
    text-transform: none !important;
    width: 100%;
    margin-top: 16px !important;
    transition: all 0.3s ease !important;

    &:hover {
        transform: translateY(-2px);
    }
`;

const Logout = () => {
    const currentUser = useSelector(state => state.user.currentUser);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleLogout = () => {
        dispatch(authLogout());
        navigate('/');
    };

    const handleCancel = () => {
        navigate(-1);
    };

    return (
        <PageWrapper>
            <Fade in timeout={800}>
                <PremiumCard>
                    <AvatarWrapper>
                        <Avatar sx={{ 
                            width: 80, 
                            height: 80, 
                            bgcolor: '#fef2f2', 
                            color: '#ef4444',
                            zIndex: 1,
                            border: '4px solid white',
                            boxShadow: '0 10px 25px rgba(239, 68, 68, 0.2)'
                        }}>
                            <LogoutIcon sx={{ fontSize: 40 }} />
                        </Avatar>
                    </AvatarWrapper>
                    
                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b', mb: 1, letterSpacing: '-0.02em' }}>
                        See you soon, {currentUser.name}!
                    </Typography>
                    
                    <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                        Are you sure you want to securely log out of your session? You will need to enter your credentials again to access the dashboard.
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <ActionBtn 
                            variant="contained" 
                            color="error" 
                            startIcon={<LogoutIcon />}
                            onClick={handleLogout}
                            sx={{ 
                                bgcolor: '#ef4444', 
                                boxShadow: '0 8px 16px rgba(239, 68, 68, 0.2) !important',
                                '&:hover': { bgcolor: '#dc2626', boxShadow: '0 12px 20px rgba(239, 68, 68, 0.3) !important' } 
                            }}
                        >
                            Yes, Log me out
                        </ActionBtn>
                        
                        <ActionBtn 
                            variant="outlined" 
                            startIcon={<CancelIcon />}
                            onClick={handleCancel}
                            sx={{ 
                                color: '#64748b', 
                                borderColor: '#cbd5e1',
                                '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' } 
                            }}
                        >
                            Cancel and return
                        </ActionBtn>
                    </Box>
                </PremiumCard>
            </Fade>
        </PageWrapper>
    );
};

export default Logout;
